"use client";

import { useState, useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  project: string;
  rating: number;
  testimonial: string;
  image: string;
  alt: string;
  videoThumbnail?: string;
  videoAlt?: string;
}

const ClientTestimonials = ({ showStats = true }: { showStats?: boolean }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScroll = useRef(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: "Priya & Rajesh Sharma",
      location: "Koramangala, Bangalore",
      project: "3BHK Apartment Transformation",
      rating: 5,
      testimonial:
        "Hayat Interiors transformed our apartment into a dream home that perfectly balances modern aesthetics with our Indian heritage. The team understood our need for a space that works for our joint family while maintaining contemporary elegance. Every detail was thoughtfully planned, from the well planned layout to the custom storage solutions.",
      image: '/assets/living-room.jpg',
      alt: "Living room",
    },
    {
      id: 2,
      name: "Arjun Mehta",
      location: "Whitefield, Bangalore",
      project: "Villa Interior Design",
      rating: 5,
      testimonial:
        "As an NRI managing this project remotely, I was initially concerned about the process. But Hayat Interiors made it seamless with regular video updates and transparent communication. The final result exceeded our expectations a beautiful blend of luxury and functionality that our entire family loves. The attention to Indian cooking needs in the kitchen design was particularly impressive.",
      image: '/assets/Living.jpg',
      alt: "Villa interior",
    },
    {
      id: 3,
      name: "Ananya & Vikram Reddy",
      location: "Indiranagar, Bangalore",
      project: "Heritage Home Restoration",
      rating: 5,
      testimonial:
        "We wanted to preserve the character of our heritage home while making it suitable for modern living. Hayat Interiors achieved this beautifully. They respected the original architecture, restored traditional elements, and seamlessly integrated modern amenities. The result is a home that tells our family's story while providing contemporary comfort. Truly exceptional craftsmanship!",
      image: '/assets/living2.jpg',
      alt: "Heritage home",
    },
    {
      id: 4,
      name: "Meera & Sunil Patel",
      location: "Jayanagar, Bangalore",
      project: "Contemporary Bungalow",
      rating: 5,
      testimonial:
        "From concept to completion, Hayat Interiors delivered a beautiful, functional bungalow that fit our family's lifestyle. Their design choices brought in light and warmth while keeping storage and utility in mind. The team was responsive and professional throughout.",
      image: '/assets/dining.jpg',
      alt: 'Dining area',
    },
    {
      id: 5,
      name: "Neha & Rohit Verma",
      location: "HSR Layout, Bangalore",
      project: "Modern Farmhouse",
      rating: 5,
      testimonial:
        "We wanted a modern farmhouse feel that still felt cozy. Hayat Interiors nailed the brief, natural textures, a cohesive palette, and clever space planning. The finished home feels effortless and inviting.",
      image: '/assets/Foyer-Area.jpg',
      alt: 'Foyer area',
    },
  ];


  useEffect(() => {
    if (!isHydrated) return;

    const el = trackRef.current;
    if (!el) return;

    const auto = setInterval(() => {
      if (!el) return;
      const next = el.scrollLeft + el.clientWidth;
      if (next >= el.scrollWidth - 4) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
        setCurrentTestimonial(0);
      } else {
        el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });
        const idx = Math.round((next) / el.clientWidth) % testimonials.length;
        setCurrentTestimonial(idx);
      }
    }, 9000);

    return () => clearInterval(auto);
  }, [isHydrated, testimonials.length]);

  const goToTestimonial = (index: number) => {
    if (!isHydrated) return;
    const el = trackRef.current;
    if (!el) {
      setCurrentTestimonial(index);
      return;
    }
    el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
    setCurrentTestimonial(index);
  };

  const prevSlide = () => {
    const el = trackRef.current;
    if (!el) return;
    const target = Math.max(0, el.scrollLeft - el.clientWidth);
    if (el.scrollLeft <= 0) {
      // wrap to end
      el.scrollTo({ left: el.scrollWidth - el.clientWidth, behavior: 'smooth' });
      setCurrentTestimonial(testimonials.length - 1);
    } else {
      el.scrollTo({ left: target, behavior: 'smooth' });
      const idx = Math.round(target / el.clientWidth) % testimonials.length;
      setCurrentTestimonial(idx);
    }
  };

  const nextSlide = () => {
    const el = trackRef.current;
    if (!el) return;
    const target = Math.min(el.scrollWidth - el.clientWidth, el.scrollLeft + el.clientWidth);
    if (target >= el.scrollWidth - 4) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
      setCurrentTestimonial(0);
    } else {
      el.scrollTo({ left: target, behavior: 'smooth' });
      const idx = Math.round(target / el.clientWidth) % testimonials.length;
      setCurrentTestimonial(idx);
    }
  };

  if (!isHydrated) {
    return (
      <section className="py-20 bg-card testimonials">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Client Testimonials
            </h2>
          </div>
          <div className="bg-background rounded-lg p-12 shadow-soft h-96" />
        </div>
      </section>);

  }

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-card testimonials">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12">
        <div className="text-center mb-10 md:mb-16">
          <p className="font-accent text-accent text-base md:text-lg mb-2 tracking-wide">What Our Clients Say</p>
          <h2 className="font-headline text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">
            Client Testimonials
          </h2>
          <p className="font-body text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
            Real stories from homeowners who trusted us with their dream spaces
          </p>
        </div>

        <div className="relative">
          <div
            ref={trackRef}
            className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory no-scrollbar"
            onPointerDown={(e) => {
              const el = trackRef.current;
              if (!el) return;
              isDragging.current = true;
              dragStartX.current = e.clientX;
              dragStartScroll.current = el.scrollLeft;
              (e.target as Element).setPointerCapture?.(e.pointerId);
            }}
            onPointerMove={(e) => {
              const el = trackRef.current;
              if (!el || !isDragging.current) return;
              const x = e.clientX;
              const walk = dragStartX.current - x;
              el.scrollLeft = dragStartScroll.current + walk;
            }}
            onPointerUp={(e) => {
              isDragging.current = false;
              try { (e.target as Element).releasePointerCapture?.(e.pointerId); } catch (err) {}
            }}
            onPointerCancel={() => { isDragging.current = false; }}
            style={{ scrollbarWidth: 'none' }}
          >
            {testimonials.map((t, idx) => (
              <article
                key={t.id}
                className="min-w-full snap-start bg-background rounded-lg overflow-hidden shadow-strong"
                aria-roledescription="slide"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="relative h-64 md:h-80 lg:h-auto">
                    <AppImage src={t.image} alt={t.alt} fill className="object-cover" />
                  </div>

                  <div className="p-6 md:p-8 lg:p-12 flex flex-col justify-center">
                    <div className="flex items-center space-x-1 mb-4 md:mb-6">
                      {[...Array(t.rating)].map((_, i) => (
                        <Icon key={i} name="StarIcon" size={20} className="text-warning md:w-6 md:h-6" variant="solid" />
                      ))}
                    </div>

                    <blockquote className="font-body text-sm md:text-base lg:text-lg text-foreground mb-6 md:mb-8 leading-relaxed">
                      {t.testimonial}
                    </blockquote>

                    <div>
                      <p className="font-headline text-lg md:text-xl font-bold text-foreground">
                        {t.name}
                      </p>
                      <p className="font-body text-xs md:text-sm text-muted-foreground">
                        {t.project}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Icon name="MapPinIcon" size={14} className="text-accent" />
                        <p className="font-body text-xs text-accent">{t.location}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <button
            onClick={prevSlide}
            aria-label="Previous testimonial"
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-full p-2 md:p-3 z-20 active:scale-95"
          >
            <Icon name="ChevronLeftIcon" size={18} className="md:w-5 md:h-5" />
          </button>

          <button
            onClick={nextSlide}
            aria-label="Next testimonial"
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-full p-2 md:p-3 z-20 active:scale-95"
          >
            <Icon name="ChevronRightIcon" size={18} className="md:w-5 md:h-5" />
          </button>

          <div className="flex justify-center space-x-2 md:space-x-3 mt-6 md:mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={`h-2 md:h-3 rounded-full transition-all duration-300 active:scale-95 ${
                  index === currentTestimonial ? 'bg-primary w-6 md:w-8' : 'w-2 md:w-3 bg-muted hover:bg-muted-foreground'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {showStats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 mt-12 md:mt-16">
          <div className="text-center">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
              <Icon name="StarIcon" size={28} className="text-primary md:w-8 md:h-8" variant="solid" />
            </div>
            <p className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-1 md:mb-2">4.9/5</p>
            <p className="font-body text-sm md:text-base text-muted-foreground">Average Rating</p>
          </div>

          <div className="text-center">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
              <Icon name="UserGroupIcon" size={28} className="text-success md:w-8 md:h-8" />
            </div>
            <p className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-1 md:mb-2">100+</p>
            <p className="font-body text-sm md:text-base text-muted-foreground">Happy Clients</p>
          </div>

          <div className="text-center">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
              <Icon name="ChatBubbleLeftRightIcon" size={28} className="text-accent md:w-8 md:h-8" />
            </div>
            <p className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-1 md:mb-2">98%</p>
            <p className="font-body text-sm md:text-base text-muted-foreground">Satisfaction Rate</p>
          </div>
        </div>
        )}
      </div>
    </section>);

};

export default ClientTestimonials;